import Head from "next/head";
import Link from "next/link";

import { routes } from "src/shared/routes";
import { constants } from "src/shared/constants";
import { useRouterExt } from "src/components/reusable/hooks/useRouterExt";
import { useTeamConfig } from "src/components/reusable/components/TeamConfigProvider";

import type { TTeamConfig } from "src/shared/types/teamConfig.types";

export const FAQS: TTeamConfig["LANGUAGES"]["FAQS"] = [
  {
    question: "How do I claim my tickets?",
    answer:
      "You will receive a text when the window to opt in is opened. The check-in window opens prior to match start.",
  },
  {
    question: "Do I get a ticket to each game on the schedule?",
    answer:
      "Tickets are guaranteed for the predetermined games in the subscription. There are no penalties for not attending a game, however you do need to confirm your attendance in order to receive a ticket. You can review your schedule on your homepage.",
  },
  {
    question: "Where are my tickets?",
    answer:
      "You will be notified when tickets are available prior to game start and are delivered within your ticketing account.",
  },
  {
    question: "How are tickets sent to me?",
    answer:
      "Prior to game start you will receive a text confirming that tickets have been delivered to your ticketing account.",
  },
  {
    question: "Where will my seats be located?",
    answer: "Seats will be located in the sections noted in your pass offer.",
  },
  {
    question: "Can these tickets be transferred to a friend?",
    answer:
      "Pass tickets, as well as seat upgrades and guest passes, are not eligible for resale or transfer. Abusing these guidelines can result in your pass being revoked.",
  },
  {
    question: "Can my seats be changed?",
    answer:
      "Once tickets have been sent to your account, they cannot be changed. Please contact support for any assistance you may require.",
  },
  {
    question: "Can I link tickets with other pass holders?",
    answer:
      "You can create a group to link with other pass holders. Navigate to the My Group tab in your menu and copy the invite. This will ensure you are seated near one another for the game. Your group must be created and all members must have joined prior to game start in order to be seated together.",
  },
  {
    question: "Can I bring a friend to a game?",
    answer:
      "Guest tickets will be available for select games. If available, guest tickets are available for purchase when you opt into games.",
  },
  {
    question: "Can I receive a refund if I cannot attend a game?",
    answer: "We do not issue any refunds for games you are not able to attend.",
  },
];

export const REGISTRATION_TERMS_AND_CONDITION = () => (
  <>
    By creating an account, you agree to receive account-related notification
    and marketing communication from the Pittsburgh Riverhounds and you confirm
    that you have read and agree to the platform&apos;s{" "}
    <Link href={"https://www.attend.tech/terms-of-use"} target={"_blank"}>
      Terms & Conditions
    </Link>{" "}
    and{" "}
    <Link href={"https://www.attend.tech/privacy-policy"} target={"_blank"}>
      Privacy Policy
    </Link>
    .
  </>
);

export const TERMS_AND_CONDITION: TTeamConfig["LANGUAGES"]["TERMS_AND_CONDITION"] =
  () => (
    <>
      By checking the box you agree to receive notifications regarding your
      purchase and to the{" "}
      <Link href={"https://www.attend.tech/terms-of-use"} target={"_blank"}>
        Terms & Conditions
      </Link>{" "}
      and{" "}
      <Link href={"https://www.attend.tech/privacy-policy"} target={"_blank"}>
        Privacy Policy
      </Link>
      .
    </>
  );

export const PASS_PURCHASE_SUCCESS_MESSAGE: TTeamConfig["LANGUAGES"]["PASS_PURCHASE_SUCCESS_MESSAGE"] =
  {
    TITLE: () => (
      <>
        You&apos;re all set to experience the action! Don&apos;t forget to
        confirm attendance here on game day!
      </>
    ),
    DESCRIPTION: () => {
      const router = useRouterExt();
      return (
        <>
          Friends don&apos;t leave friends behind.{" "}
          <b>
            <u>
              <span
                onClick={() => router.pushSchema(routes.my_group)}
                style={{ cursor: "pointer" }}
              >
                Invite your friends
              </span>
            </u>
          </b>{" "}
          and enjoy games together.
        </>
      );
    },
  };

export const AnalyticsHeader = () => {
  const { TEAM_SCHEMA } = useTeamConfig();

  return (
    <Head>
      <script
        defer
        nonce={process.env.NEXT_PUBLIC_CSP_NONCE}
        id={`${TEAM_SCHEMA}-pixel-script`}
        dangerouslySetInnerHTML={{
          __html: `
              !function(f,b,e,v,n,t,s)
              {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
              n.callMethod.apply(n,arguments):n.queue.push(arguments)};
              if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
              n.queue=[];t=b.createElement(e);t.async=!0;
              t.src=v;s=b.getElementsByTagName(e)[0];
              s.parentNode.insertBefore(t,s)}(window, document,'script',
              'https://connect.facebook.net/en_US/fbevents.js');
              fbq('init', '1485782749995063');
              fbq('track', 'PageView');
              window.addEventListener("${constants.customEvents.purchaseSuccess}", function(e) {
                const { cartRes } = e.detail;
                fbq('track', 'Purchase', {
                  value: cartRes.totalAmount,
                  currency: 'USD',
                });
                console.log('meta pixel purchase event fired.');
              });
            `,
        }}
      />

      <noscript>
        <img
          height="1"
          width="1"
          style={{ display: "none" }}
          src="https://www.facebook.com/tr?id=1485782749995063&ev=PageView&noscript=1"
        />
      </noscript>
    </Head>
  );
};
